import { useQuery } from "@tanstack/react-query";
import apiService from "../../services/api";
import { LIVE_GC_TIME, LIVE_STALE_TIME } from "./utils";

export type ExplorerLiveStreamRow = {
  _id: string;
  videoId?: string;
  channelId?: string;
  channelName?: string;
  title?: string;
  actualStartTime?: string;
  currentViewers?: number;
  peakViewers?: number;
};

type MergeableLivestream = {
  _id: string;
  livestreamId?: string;
  status?: string;
  peakViewers?: number;
  averageViewers?: number;
  title?: string;
  actualStartTime?: string;
  actualEndTime?: string | null;
  videoId?: string;
  channelId?: string;
  channelName?: string;
};

function segmentHasEnded(row: MergeableLivestream): boolean {
  if (row.actualEndTime == null || row.actualEndTime === "") return false;
  const ended = new Date(row.actualEndTime).getTime();
  return Number.isFinite(ended);
}

const LIVE_STATUSES = new Set(["live", "processing"]);

/** Polls currently live streams while the page is open. Historical queries stay cached. */
export function useExplorerLiveStreamsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ["explorer-live-streams", "all"],
    queryFn: () => apiService.getLiveStreams(),
    staleTime: LIVE_STALE_TIME,
    gcTime: LIVE_GC_TIME,
    refetchInterval: LIVE_STALE_TIME,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function mergeExplorerLivestreamsWithLive<T extends MergeableLivestream>(
  rows: T[],
  liveStreams: ExplorerLiveStreamRow[] | undefined,
  liveQueryReady: boolean,
  scope?: { youtubeChannelId?: string },
): T[] {
  if (!liveQueryReady || !liveStreams) return rows;

  const liveById = new Map(
    liveStreams.map((stream) => [String(stream._id), stream]),
  );
  const merged = rows.map((row) => {
    const live =
      liveById.get(String(row._id)) ??
      (row.livestreamId ? liveById.get(String(row.livestreamId)) : undefined);
    if (live) {
      if (segmentHasEnded(row)) {
        return row.status && LIVE_STATUSES.has(row.status)
          ? { ...row, status: "completed" }
          : row;
      }
      return {
        ...row,
        status: "live",
        peakViewers: live.peakViewers || row.peakViewers,
        averageViewers: live.currentViewers ?? row.averageViewers,
      };
    }
    if (row.status && LIVE_STATUSES.has(row.status)) {
      return { ...row, status: "completed" };
    }
    return row;
  });

  const sameRows =
    merged.length === rows.length &&
    merged.every((row, index) => row === rows[index]);
  const youtubeChannelId = scope?.youtubeChannelId?.trim();
  if (!youtubeChannelId) return sameRows ? rows : merged;

  const knownIds = new Set(
    merged.flatMap((row) =>
      [row._id, row.livestreamId].filter((id): id is string => Boolean(id)).map(String),
    ),
  );
  const extras = liveStreams.filter(
    (stream) =>
      !knownIds.has(String(stream._id)) &&
      stream.channelId?.trim() === youtubeChannelId,
  );
  if (extras.length === 0) return sameRows ? rows : merged;

  return [
    ...extras.map(
      (stream) =>
        ({
          _id: String(stream._id),
          videoId: stream.videoId ?? "",
          title: stream.title ?? "",
          status: "live",
          actualStartTime: stream.actualStartTime,
          peakViewers: stream.peakViewers,
          averageViewers: stream.currentViewers,
          channelId: stream.channelId,
          channelName: stream.channelName,
        }) as T,
    ),
    ...merged,
  ];
}
